<template>
  <main v-if="data" class="container">
    <div class="row">
      <div class="col-12 col-xs-10 col-md-12 col-lg-10 col-xl-8 col-xxl-7 mx-auto mt-4 mt-md-5">
        <div>
          <h1 class="text-start">
            <span class="inherit badge bg-primary rounded-pill mb-2">{{ data.registration }}</span>
            <span class="h2 d-block">{{ ' ' + data.model }}</span>
          </h1>
          <p v-if="data.nickname" class="text-secondary fst-italic mb-0">Nickname: {{ data.nickname }}</p>
        </div>
        <div class="row mt-3">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              About {{ data.registration }} - {{ data.model }}
            </h2>
            <div class="row g-2 mb-3">
              <div class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Registration</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    {{ data.registration }}
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Serial</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    {{ data.serial ?? '-' }}
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Model</h3>
                  <p class="mb-0 fw-bold mb-n1 truncate">
                    {{ data.model }}
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Status</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    Active
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Age</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    {{ data.age ? formatAge(data.age) + ' years' : '-' }}
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Last Seen</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    {{ lastFlight ? formatDate(lastFlight.scheduled_departure) : '-' }}
                    <span class="small opacity-75" v-if="lastFlight">({{ lastFlight.flight_number }})</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              Operator
            </h2>
            <div class="row g-2 mb-3">
              <div class="col-12 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">IATA</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    <NuxtImg
                        height="18"
                        v-if="data.owner.logo_small_url"
                        class="me-1 mt-n1"
                        loading="lazy"
                        :src="data.owner.logo_small_url"
                        :alt="data.owner.name + ' icon'"
                    />
                    <NuxtLink
                        :to="{ name: 'airlines-slug', params: { slug: data.owner.slug }}"
                        class="inherit fw-bold ms-1"
                        :title="data.owner.name">
                      {{ data.owner.short_name ?? data.owner.name }}
                    </NuxtLink>
                  </p>
                </div>
              </div>
              <div class="col-12 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">ICAO</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    {{ data.owner.icao }}
                  </p>
                </div>
              </div>
              <div v-if="data.owner.country" class="col-12 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Country</h3>
                  <p class="mb-0 fw-bold mb-n1">
                    {{ data.owner.country.emoji }} {{ data.owner.country.name }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="data.flights.filter(flight => flight.is_live).length" class="row mb-4">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              Live Flights
            </h2>
          </div>
          <flight-table
              v-if="data.flights.length"
              :limit="3"
              :live-only="true"
              :show-load-more="true"
              :flights="data.flights.filter(flight => flight.is_live).reverse()"
          />
        </div>

        <div v-else-if="data.flights.filter(flight => flight.status === 'Scheduled').length" class="row mb-4">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              Upcoming Flights
            </h2>
          </div>
          <flight-table
              v-if="data.flights.length"
              :limit="3"
              :show-load-more="true"
              :flights="data.flights.filter(flight => flight.status === 'Scheduled').reverse()"
          />
        </div>

        <div v-if="data.flights.length" class="row mb-4">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              Past Flights
            </h2>
            <div v-if="data.owner.frequent_flyer_program !== 'Flying Blue'" class="alert alert-warning">
              <p class="small mb-0"><i class="fa-solid fa-circle-info ps-1 pe-1"></i>  We only show Flying Blue related flights for this aircraft.</p>
            </div>
          </div>
          <flight-table
              v-if="data.flights.length"
              :show-load-more="false"
              :flights="data.flights.filter(flight => flight.status !== 'Scheduled' && !flight.is_live)"
          />
        </div>

        <div v-if="data.stats && data.stats.top_destinations" class="row mb-2">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              Popular Destinations
            </h2>
            <div class="card no-hover bg-blurry mb-2 p-3">
              <p class="mb-0 opacity-75 small">
                <i class="fa-solid fa-circle-info ps-1 pe-1"></i> {{ data.registration }} destinations in the past <u>30 days</u>:
              </p>
            </div>
            <div v-if="data.owner.frequent_flyer_program !== 'Flying Blue'" class="alert alert-warning mb-2">
              <p class="small mb-0"><i class="fa-solid fa-circle-info ps-1 pe-1"></i>  We only count Flying Blue related flights for this aircraft.</p>
            </div>
            <div class="row g-2 mb-3">
              <div class="col-12 col-sm-6 col-md-4" v-for="destination in data.stats.top_destinations">
                <div class="card no-hover d-flex flex-row align-items-center p-3 bg-blurry">
                  <div>
                    <p class="mb-0 index bg-blurry border fw-bold">
                      {{ destination.count }}x
                    </p>
                  </div>
                  <div>
                    <p class="mb-1 small text-uppercase opacity-75 fw-normal">{{ destination.destination.city.country.name }}</p>
                    <h3 class="mb-0 h6 fw-bold mb-n1 truncate">
                      {{ destination.destination.city.country.emoji }} {{ destination.destination.city.name }}
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="data.stats && data.stats.total > 0" class="row mb-4">
          <div class="col-12 mt-3">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-3">
              {{ data.registration }} Statistics
            </h2>
            <div class="card no-hover bg-blurry mb-2 p-3">
              <p class="mb-0 opacity-75 small">
                <i class="fa-solid fa-circle-info ps-1 pe-1"></i> {{ data.registration }} statistics in the past <u>30 days</u>:
              </p>
            </div>
            <div class="row g-2 mb-3">
              <div class="col-6 col-md-4" >
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Flights</h3>
                  <p class="mb-0 fw-bold mb-n1 truncate">
                    {{ Intl.NumberFormat("nl").format(data.stats.total) }} ({{ Intl.NumberFormat("nl", { maximumSignificantDigits: 2 }).format(data.stats.total / 30) }} per day)
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-4" >
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Operational hours</h3>
                  <p class="mb-0 fw-bold mb-n1 truncate">
                    {{ Math.round(data.stats.total_duration / 60) }} ({{ Intl.NumberFormat("nl", { maximumSignificantDigits: 2 }).format(data.stats.avg_duration / 60) }} per flight)
                  </p>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-md-4">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Distance
                    <span role="button" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title="Based on original flight route"><i class="fa-solid fa-circle-info ps-1 pe-1"></i></span></h3>
                  <p class="mb-0 fw-bold mb-n1 truncate">
                    {{ Intl.NumberFormat("nl", { maximumSignificantDigits: 1 }).format(data.stats.total_distance) }}km ({{ Intl.NumberFormat("nl", { maximumSignificantDigits: 2 }).format(data.stats.avg_distance) }} per flight)
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Delayed Departures</h3>
                  <p class="mb-0 fw-bold mb-n1 truncate">
                    {{ Intl.NumberFormat("nl").format(data.stats.total_flights_departure_delay) }} ({{ 100 - data.stats.avg_flights_depart_on_time}}% on time)
                  </p>
                </div>
              </div>
              <div class="col-6 col-md-6">
                <div class="card no-hover p-3 bg-blurry">
                  <h3 class="mb-1 small text-uppercase opacity-75 fw-normal">Delayed Arrivals</h3>
                  <p class="mb-0 fw-bold mb-n1 truncate">
                    {{ Intl.NumberFormat("nl").format(data.stats.total_flights_arrival_delay) }} ({{ 100 - data.stats.avg_flights_arrive_on_time}}% on time)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-6 gy-2" itemscope itemtype="https://schema.org/FAQPage">
          <div class="col-12">
            <h2 class="h6 small fw-bold text-uppercase text-primary mb-1">Frequently Asked Questions</h2>
          </div>
          <Question>
            <template v-slot:question>
              What is the registration number of this aircraft?
            </template>
            <template v-slot:answer>
              <p class="mb-0">The registration number of this {{ data.manufacturer }} aircraft is {{ data.registration }}.</p>
            </template>
          </Question>
          <Question v-if="data.owner">
            <template v-slot:question>
              Which airline owns this aircraft?
            </template>
            <template v-slot:answer>
              <p class="mb-0">This aircraft is owned by {{ data.owner.name }}.</p>
            </template>
          </Question>
          <Question>
            <template v-slot:question>
              What is the type and model of this aircraft?
            </template>
            <template v-slot:answer>
              <p class="mb-0">The {{ data.registration }} of {{ data.owner?.name }} is a {{ data.model }}.</p>
            </template>
          </Question>
          <Question v-if="data.flights">
            <template v-slot:question>
              What are the recent destinations and history of this aircraft?
            </template>
            <template v-slot:answer>
              <p>The recent destinations of this {{ data.registration }} {{ data.manufacturer }} are:</p>
              <ul class="mb-0">
                <li v-for="flights in data.flights">
                  {{ flights.flight_number }} from {{ flights.origin.city.name }} ({{ flights.origin.iata }}) to {{ flights.destination.city.name }} ({{ flights.destination.iata }})
                </li>
              </ul>
            </template>
          </Question>
          <Question v-if="data.age">
            <template v-slot:question>
              How old is this aircraft?
            </template>
            <template v-slot:answer>
              <p class="mb-0">This {{ data.manufacturer }} aircraft of {{ data.owner?.name }} is {{ formatAge(data.age) }} years old.</p>
              <p v-if="formatAge(data.age) > 10" class="mt-2">The safety of aircraft is always a priority for {{ data.owner?.name }}, regardless of the age of the aircraft. This {{ data.manufacturer }} of {{ formatAge(data.age) }} years is safe and is not yet due for retirement. Moreover, the interior is often upgraded with new seats more than a new aircraft is ordered, for extra comfort.</p>
            </template>
          </Question>
          <Question v-if="data.stats && data.stats.top_destinations">
            <template v-slot:question>
              What destinations does {{ data.owner?.short_name ?? data.owner?.name ?? 'the airline' }} serve with the {{ data.manufacturer }} {{ data.registration }}?
            </template>
            <template v-slot:answer>
              <p class="mb-2">
                In the past 30 days, the {{ data.registration }} from {{ data.owner?.short_name ?? data.owner?.name ?? 'the airline' }} has made {{ data.stats?.total ?? 0 }} flights to various destinations. The {{ data.stats.top_destinations.length }} most popular {{ data.registration }} destinations were:
              </p>
              <ul>
                <li v-for="destination in data.stats.top_destinations">
                  {{ destination.count }} times to {{ destination.destination.city.name }} (<nuxtLink :title="(data.owner?.short_name ?? data.owner?.name ?? '') + ' flights to ' + destination.destination.name_en + ' (' + destination.destination.city.name + ')'" :to="{ name: 'flights-to', params: { name: destination.destination.city.slug_en, iata: destination.destination.iata.toLowerCase() } }">{{ destination.destination.name_en }}</nuxtLink>).
                </li>
              </ul>
              <p class="mb-0 small">In our calculations, we only include commercial flights related to Flying Blue where {{ data.registration }} has reached its destination.</p>
            </template>
          </Question>
        </div>
      </div>
    </div>
  </main>
</template>
<script setup lang="ts">
import moment from "moment/moment";

const route = useRoute()
const runtimeConfig = useRuntimeConfig()

const {data: fetchedData} = await useAsyncData(
    'aircraft-' + route.params.slug,
    async () => {
      return $fetch(runtimeConfig.public.backendUrl + "/api/v2/aircrafts/" + route.params.slug, {
        headers: {
          'User-Agent': 'RewardsFlightsFrontend/1.0 (https://rewards.flights)',
        },
        params: {
          include: 'stats',
        }
      })
    }
);

if (!fetchedData.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Aircraft not found'
  })
}

const data = ref(fetchedData.value.aircraft);

defineOgImage({
  component: 'route',
  title: data.value.model,
  description: data.value.registration,
});

useSeoMeta({
  title: data.value.registration + ' - ' + data.value.model + ' from ' + data.value.owner.name,
  ogTitle: data.value.registration + ' - ' + data.value.model + ' from ' + data.value.owner.name,
  twitterTitle: data.value.registration + ' - ' + data.value.model + ' from ' + data.value.owner.name,
  description: 'Discover the ' + data.value.model + ' from ' + data.value.owner.name + ' with registration ' + data.value.registration + ' (' + data.value.serial + '). View flights, history, seat map, and more of this aircraft.',
  ogDescription: 'Discover the ' + data.value.model + ' from ' + data.value.owner.name + ' with registration ' + data.value.registration + ' (' + data.value.serial + '). View flights, history, seat map, and more of this aircraft.',
  twitterDescription: 'Discover the ' + data.value.model + ' from ' + data.value.owner.name + ' with registration ' + data.value.registration + ' (' + data.value.serial + '). View flights, history, seat map, and more of this aircraft.',
  ogUrl: runtimeConfig.public.baseUrl + "/aircrafts/" + data.value.slug,
  ogImage: data.value.logo_url,
  twitterImage: data.value.logo_url,
  twitterImageAlt: 'Logo',
  ogLocale: "en",
  ogLocaleAlternate: "nl",
});

useHead({
  htmlAttrs: {
    lang: 'en'
  },
  link: [
    {
      rel: "canonical",
      href: runtimeConfig.public.baseUrl + "/aircrafts/" + data.value.slug
    },
    {
      rel: "alternate",
      hreflang: "x-default",
      href: runtimeConfig.public.baseUrl + "/aircrafts/" + data.value.slug
    },
    {
      rel: "alternate",
      hreflang: "en",
      href: runtimeConfig.public.baseUrl + "/aircrafts/" + data.value.slug
    },
    {
      rel: "alternate",
      hreflang: "nl",
      href: runtimeConfig.public.baseUrl + "/nl/vliegtuigen/" + data.value.slug
    }
  ],
});

useJsonld(() => {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: runtimeConfig.public.baseUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Luchtvaartmaatschappijen',
            item: runtimeConfig.public.baseUrl + '/airlines',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: data.value.owner.name,
            item: runtimeConfig.public.baseUrl + '/airlines/' + data.value.owner.slug,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: data.value.registration + ' - ' + data.value.model,
          },
        ],
      },
      {
        '@type': 'Vehicle',
        name: data.value.model,
        model: data.value.model.replace(data.value.manufacturer,''),
        manufacturer: {
          '@type': 'Organization',
          name: data.value.manufacturer,
        },
        category: 'Aircraft',
        productionDate: data.value.age,
        fuelType: 'Aviation gasoline',
        productID: data.value.registration,
      }
    ]
  }
});

const lastFlight = computed(() => {
  return data.value.flights.find(flight => flight.is_live || flight.is_landed) || null;
});

function formatDate(date: string | null) {
  if (date === null) {
    return null;
  }

  const latestFlightDate = moment(date);
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'days').startOf('day');

  if (latestFlightDate.isSame(today, 'day')) {
    return 'Today';
  } else if (latestFlightDate.isSame(yesterday, 'day')) {
    return 'Yesterday';
  } else {
    return latestFlightDate.format('D/M/Y');
  }
}

function formatAge(age: string | null) {
  if (age === null) {
    return '-';
  }

  const ageDate = moment(age);
  const now = moment();

  return now.diff(ageDate, 'years');
}
</script>